import { useState } from 'react';
import { api, type Role, type User } from '../../api';
import { can, useData, useMe } from '../../useData';
import { ErrorMsg, Modal } from '../../components/Modal';
import { RoleSheet } from './RoleSheet';
import { Avatar } from '../../components/Avatar';

export function UsersPage() {
  const me = useMe();
  const { data: users, error, reload } = useData<User[]>('/admin/users');
  const { data: roles } = useData<Role[]>('/admin/roles');
  const [editing, setEditing] = useState<User | 'new' | null>(null);
  const [sheetFor, setSheetFor] = useState<User | null>(null);
  const [passwordFor, setPasswordFor] = useState<User | null>(null);
  const [inviteFor, setInviteFor] = useState<User | null>(null);
  const [confirmDel, setConfirmDel] = useState<number | null>(null);
  const [delError, setDelError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  const systemRoles = (roles ?? []).filter((r) => r.scope === 'System');
  const roleNames = (ids: number[]) => systemRoles.filter((r) => ids.includes(r.id)).map((r) => r.name).join(', ');
  const nameOf = (id: number | null) => users?.find((u) => u.id === id)?.displayName ?? '';
  const shown = (users ?? []).filter((u) =>
    `${u.displayName} ${u.email}`.toLowerCase().includes(filter.toLowerCase()));

  return (
    <>
      <div className="page-head">
        <h1>Utilizatori</h1>
        {can(me, 'admin.users.manage') && <button className="primary" onClick={() => setEditing('new')}>+ Utilizator nou</button>}
      </div>
      <ErrorMsg error={error} />
      <ErrorMsg error={delError} />
      <input className="search" placeholder="Caută după nume sau e-mail…" value={filter} onChange={(e) => setFilter(e.target.value)} />
      <table>
        <thead>
          <tr><th>Nume</th><th>E-mail</th><th>Rol</th><th>Supervizor</th><th>Login</th><th>Fișa de rol</th><th>Ultima autentificare</th><th>Stare</th><th /></tr>
        </thead>
        <tbody>
          {shown.map((u) => (
            <tr key={u.id} className={u.isActive ? '' : 'inactive'}>
              <td>
                <div className="user-cell">
                  <Avatar userId={u.id} name={u.displayName} hasAvatar={u.hasAvatar} size={30} />
                  <div>{u.displayName}{u.jobTitle && <div className="muted small">{u.jobTitle}</div>}</div>
                </div>
              </td>
              <td className="muted">{u.email}</td>
              <td>{roleNames(u.roleIds)}</td>
              <td>{nameOf(u.supervisorId)}</td>
              <td className="small">
                {[u.hasMicrosoft && 'Microsoft', u.allowPasswordLogin && u.hasPassword && 'parolă'].filter(Boolean).join(' + ') ||
                  (u.allowPasswordLogin ? <span className="muted">invitație în așteptare</span> : <span className="muted">încă nu s-a logat</span>)}
                {u.lockedUntil && new Date(u.lockedUntil) > new Date() && <span className="tag warn">blocat</span>}
              </td>
              <td>{u.overrideCount > 0 ? <span className="tag warn">{u.overrideCount} excepții</span> : <span className="muted">standard</span>}</td>
              <td className="muted small">{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString('ro-RO') : 'niciodată'}</td>
              <td>{u.isActive ? <span className="tag ok">Activ</span> : <span className="tag">Inactiv</span>}</td>
              <td className="right nowrap">
                {can(me, 'admin.users.manage') && <button className="link" onClick={() => setEditing(u)}>Editează</button>}
                {can(me, 'admin.users.manage') && u.isActive && <button className="link" onClick={() => setInviteFor(u)}>Invitație</button>}
                {can(me, 'admin.users.manage') && <button className="link" onClick={() => setPasswordFor(u)}>Parolă</button>}
                {can(me, 'admin.roles.manage') && <button className="link" onClick={() => setSheetFor(u)}>Fișa de rol</button>}
                {can(me, 'admin.users.manage') && u.id !== me?.id && (
                  confirmDel === u.id ? (
                    <button className="link danger" onClick={async () => {
                      setConfirmDel(null);
                      try { await api.del(`/admin/users/${u.id}`); reload(); } catch (e) { setDelError((e as Error).message); }
                    }}>Confirmați ștergerea</button>
                  ) : (
                    <button className="link danger" onClick={() => { setConfirmDel(u.id); setDelError(null); }}>Șterge</button>
                  )
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing && (
        <UserForm user={editing === 'new' ? null : editing} roles={systemRoles} users={users ?? []}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />
      )}
      {passwordFor && <PasswordModal user={passwordFor} onClose={() => setPasswordFor(null)} onSaved={() => { setPasswordFor(null); reload(); }} />}
      {inviteFor && <InviteModal user={inviteFor} onClose={() => { setInviteFor(null); reload(); }} />}
      {sheetFor && <RoleSheet user={sheetFor} onClose={() => setSheetFor(null)} onSaved={() => { setSheetFor(null); reload(); }} />}
    </>
  );
}

function UserForm(props: { user: User | null; roles: Role[]; users: User[]; onClose: () => void; onSaved: () => void }) {
  const u = props.user;
  const defaultRole = props.roles.find((r) => r.code === 'sys.user')?.id;
  const [email, setEmail] = useState(u?.email ?? '');
  const [displayName, setDisplayName] = useState(u?.displayName ?? '');
  const [jobTitle, setJobTitle] = useState(u?.jobTitle ?? '');
  const [phone, setPhone] = useState(u?.phone ?? '');
  const [supervisorId, setSupervisorId] = useState<number | null>(u?.supervisorId ?? null);
  const [isActive, setIsActive] = useState(u?.isActive ?? true);
  const [allowPasswordLogin, setAllowPasswordLogin] = useState(u?.allowPasswordLogin ?? false);
  const [roleId, setRoleId] = useState<number | undefined>(u?.roleIds[0] ?? defaultRole);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const supervisorRoleIds = props.roles.filter((r) => r.code === 'sys.supervisor' || r.code === 'sys.admin').map((r) => r.id);
  const supervisors = props.users.filter((x) => x.isActive && x.id !== u?.id && x.roleIds.some((r) => supervisorRoleIds.includes(r)));

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const body = {
        email: email.trim(), displayName: displayName.trim(), jobTitle: jobTitle || null, phone: phone || null,
        supervisorId, isActive, allowPasswordLogin, roleIds: roleId ? [roleId] : [],
      };
      if (u) await api.put(`/admin/users/${u.id}`, body);
      else await api.post('/admin/users', body);
      props.onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={u ? `Editează: ${u.displayName}` : 'Utilizator nou'} onClose={props.onClose}>
      <p className="muted small">Colegii cu cont Microsoft 365 intră cu e-mailul de firmă. Pentru colaboratorii fără cont Microsoft bifați opțiunea de mai jos și apoi trimiteți-le „Invitație” pe e-mail.</p>
      <label>E-mail<input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus={!u} /></label>
      <label>Nume afișat<input value={displayName} onChange={(e) => setDisplayName(e.target.value)} /></label>
      <div className="row">
        <label>Funcție<input value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} /></label>
        <label>Telefon<input value={phone} onChange={(e) => setPhone(e.target.value)} /></label>
      </div>
      <label>Nivel de acces
        <select value={roleId} onChange={(e) => setRoleId(Number(e.target.value))}>
          {props.roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
      </label>
      <label>Supervizor
        <select value={supervisorId ?? 0} onChange={(e) => setSupervisorId(Number(e.target.value) || null)}>
          <option value={0}>— fără —</option>
          {supervisors.map((s) => <option key={s.id} value={s.id}>{s.displayName}</option>)}
        </select>
      </label>
      <label className="checkbox">
        <input type="checkbox" checked={allowPasswordLogin} onChange={(e) => setAllowPasswordLogin(e.target.checked)} />
        Permite autentificarea cu e-mail și parolă (prin „Invitație” pe e-mail sau din butonul „Parolă”)
      </label>
      {u && (
        <label className="checkbox"><input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} /> Cont activ</label>
      )}
      <ErrorMsg error={error} />
      <div className="actions">
        <button onClick={props.onClose}>Renunță</button>
        <button className="primary" disabled={busy || !email || displayName.trim().length < 2 || !roleId} onClick={save}>Salvează</button>
      </div>
    </Modal>
  );
}

/** Generează o parolă temporară ușor de dictat: 3 grupuri de litere + cifre. */
function tempPassword() {
  const letters = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ';
  const pick = (n: number, set: string) => Array.from(crypto.getRandomValues(new Uint32Array(n)), (x) => set[x % set.length]).join('');
  return `${pick(4, letters)}-${pick(4, '23456789')}-${pick(4, letters)}`;
}

function PasswordModal({ user, onClose, onSaved }: { user: User; onClose: () => void; onSaved: () => void }) {
  const [password, setPassword] = useState(tempPassword());
  const [mustChange, setMustChange] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const locked = !!user.lockedUntil && new Date(user.lockedUntil) > new Date();

  async function save() {
    setError(null);
    try {
      await api.post(`/admin/users/${user.id}/password`, { password, mustChange });
      setDone(true);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <Modal title={`Parolă: ${user.displayName}`} onClose={done ? onSaved : onClose}>
      {done ? (
        <>
          <p>Parola a fost setată, iar autentificarea cu parolă este activă pentru {user.email}.</p>
          <p>Transmiteți-i parola pe un canal sigur{mustChange ? '; la prima autentificare i se va cere să o schimbe' : ''}:</p>
          <p className="mono big-code">{password}</p>
          <div className="actions"><button className="primary" onClick={onSaved}>Gata</button></div>
        </>
      ) : (
        <>
          <p className="muted small">
            {user.hasPassword ? 'Utilizatorul are deja o parolă; cea nouă o înlocuiește.' : 'Utilizatorul nu are încă parolă.'}
            {' '}Autentificarea cu contul Microsoft rămâne disponibilă.
          </p>
          <label>Parolă (temporară)
            <div className="row">
              <input className="mono" value={password} onChange={(e) => setPassword(e.target.value)} />
              <button onClick={() => setPassword(tempPassword())}>Generează</button>
            </div>
          </label>
          <label className="checkbox"><input type="checkbox" checked={mustChange} onChange={(e) => setMustChange(e.target.checked)} /> Cere schimbarea parolei la prima autentificare</label>
          {locked && (
            <p className="muted small">Contul este blocat temporar după încercări greșite.{' '}
              <button className="link" onClick={async () => { await api.post(`/admin/users/${user.id}/unlock`, {}); onSaved(); }}>Deblochează acum</button>
            </p>
          )}
          <ErrorMsg error={error} />
          <div className="actions">
            <button onClick={onClose}>Renunță</button>
            <button className="primary" onClick={save}>Setează parola</button>
          </div>
        </>
      )}
    </Modal>
  );
}

/** Trimite pe e-mail (de la noreply@morogroup.ro) linkul de activare a contului cu parolă. */
function InviteModal({ user, onClose }: { user: User; onClose: () => void }) {
  const [result, setResult] = useState<{ sent: boolean; email?: string; link?: string; error?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function send() {
    setBusy(true);
    setError(null);
    try {
      setResult(await api.post(`/admin/users/${user.id}/invite`, {}));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal title={`Invitație: ${user.displayName}`} onClose={onClose}>
      {!result && (
        <>
          <p>Se trimite la <strong>{user.email}</strong> un e-mail cu un link de activare (valabil 72 de ore, de unică folosință),
            prin care utilizatorul își alege singur parola. Contul va putea intra cu e-mail și parolă.</p>
          {user.allowPasswordLogin && user.hasPassword && <p className="muted small">Utilizatorul are deja o parolă; linkul îi permite să și-o schimbe.</p>}
        </>
      )}
      {result?.sent && <p className="tag ok">E-mail trimis la {result.email}.</p>}
      {result && !result.sent && (
        <>
          <p className="error">E-mailul nu a putut fi trimis: {result.error}</p>
          <p>Puteți trimite manual acest link utilizatorului (de exemplu pe WhatsApp):</p>
          <input className="mono" readOnly value={result.link} onFocus={(e) => e.target.select()} />
          <button className="link" onClick={() => navigator.clipboard?.writeText(result.link ?? '')}>Copiază linkul</button>
        </>
      )}
      <ErrorMsg error={error} />
      <div className="actions">
        <button onClick={onClose}>{result ? 'Închide' : 'Renunță'}</button>
        {!result && <button className="primary" disabled={busy} onClick={send}>{busy ? 'Se trimite…' : 'Trimite invitația'}</button>}
      </div>
    </Modal>
  );
}
